// Fake coverage data for the brand portal's My Campaigns page.
//
// Real brands on dev/stage reach a handful of screens in one or two cities,
// which never exercises the map's clustering, the city -> area accordion or the
// status counts at scale. This builds a worldwide fleet (~240 screens) with the
// exact shape `brandScreenMap` returns, so the page can be judged the way a big
// brand would see it.
//
// Off unless asked for: REACT_APP_BRAND_DEMO_DATA=true at build time, or
// `?demo=1` in the URL on a development server (`npm start`). Never switch it on
// for a build a real brand signs in to.

import type {
  BrandScreenMap,
  CampaignStatus,
  CampaignSummary,
  MapCampaign,
  MapScreen,
  MapSlot,
} from "../schemas/campaignSchema";

export const isBrandDemoData = (): boolean => {
  if (process.env.REACT_APP_BRAND_DEMO_DATA === "true") return true;
  if (process.env.NODE_ENV !== "development") return false;
  try {
    return new URLSearchParams(window.location.search).has("demo");
  } catch {
    return false;
  }
};

/** Deterministic PRNG (mulberry32) so every reload shows the same fleet. */
const rng = (seed: number) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

interface CitySeed {
  city: string;
  /** City centre. */
  lat: number;
  lng: number;
  /** Rough share of the fleet — the brand's home market is heavier. */
  weight: number;
  areas: string[];
}

const CITIES: CitySeed[] = [
  // Home market
  { city: "Mumbai", lat: 19.076, lng: 72.8777, weight: 22, areas: ["Bandra West", "Andheri East", "Lower Parel", "Powai", "Colaba", "Juhu"] },
  { city: "Delhi", lat: 28.6139, lng: 77.209, weight: 20, areas: ["Connaught Place", "Saket", "Hauz Khas", "Karol Bagh", "Vasant Kunj"] },
  { city: "Bengaluru", lat: 12.9716, lng: 77.5946, weight: 18, areas: ["Indiranagar", "Koramangala", "Whitefield", "MG Road", "HSR Layout"] },
  { city: "Hyderabad", lat: 17.385, lng: 78.4867, weight: 10, areas: ["Banjara Hills", "HITEC City", "Gachibowli", "Jubilee Hills"] },
  { city: "Chennai", lat: 13.0827, lng: 80.2707, weight: 8, areas: ["T. Nagar", "Adyar", "Anna Nagar", "Velachery"] },
  { city: "Pune", lat: 18.5204, lng: 73.8567, weight: 7, areas: ["Koregaon Park", "Hinjewadi", "Viman Nagar"] },
  { city: "Kolkata", lat: 22.5726, lng: 88.3639, weight: 6, areas: ["Park Street", "Salt Lake", "Ballygunge"] },
  { city: "Gurugram", lat: 28.4595, lng: 77.0266, weight: 6, areas: ["Cyber Hub", "Golf Course Road", "Sohna Road"] },
  { city: "Ahmedabad", lat: 23.0225, lng: 72.5714, weight: 4, areas: ["SG Highway", "Navrangpura"] },
  { city: "Jaipur", lat: 26.9124, lng: 75.7873, weight: 3, areas: ["C-Scheme", "Malviya Nagar"] },
  // Gulf
  { city: "Dubai", lat: 25.2048, lng: 55.2708, weight: 12, areas: ["Downtown", "Dubai Marina", "JLT", "Deira", "Business Bay"] },
  { city: "Abu Dhabi", lat: 24.4539, lng: 54.3773, weight: 5, areas: ["Corniche", "Yas Island", "Al Reem"] },
  { city: "Doha", lat: 25.2854, lng: 51.531, weight: 4, areas: ["West Bay", "The Pearl"] },
  { city: "Riyadh", lat: 24.7136, lng: 46.6753, weight: 4, areas: ["Olaya", "Al Malqa"] },
  // Asia-Pacific
  { city: "Singapore", lat: 1.3521, lng: 103.8198, weight: 8, areas: ["Orchard", "Marina Bay", "Tanjong Pagar", "Jurong East"] },
  { city: "Kuala Lumpur", lat: 3.139, lng: 101.6869, weight: 4, areas: ["Bukit Bintang", "KLCC", "Bangsar"] },
  { city: "Bangkok", lat: 13.7563, lng: 100.5018, weight: 4, areas: ["Sukhumvit", "Siam", "Silom"] },
  { city: "Tokyo", lat: 35.6762, lng: 139.6503, weight: 5, areas: ["Shibuya", "Shinjuku", "Ginza"] },
  { city: "Sydney", lat: -33.8688, lng: 151.2093, weight: 5, areas: ["CBD", "Surry Hills", "Bondi"] },
  { city: "Melbourne", lat: -37.8136, lng: 144.9631, weight: 3, areas: ["CBD", "Fitzroy"] },
  // Europe
  { city: "London", lat: 51.5074, lng: -0.1278, weight: 10, areas: ["Soho", "Shoreditch", "Canary Wharf", "Camden", "Southall"] },
  { city: "Manchester", lat: 53.4808, lng: -2.2426, weight: 3, areas: ["Northern Quarter", "Rusholme"] },
  { city: "Paris", lat: 48.8566, lng: 2.3522, weight: 4, areas: ["Le Marais", "La Défense", "Montmartre"] },
  { city: "Berlin", lat: 52.52, lng: 13.405, weight: 3, areas: ["Mitte", "Kreuzberg"] },
  { city: "Amsterdam", lat: 52.3676, lng: 4.9041, weight: 2, areas: ["De Pijp", "Centrum"] },
  // Americas
  { city: "New York", lat: 40.7128, lng: -74.006, weight: 9, areas: ["Midtown", "Jackson Heights", "Williamsburg", "Lower East Side"] },
  { city: "Toronto", lat: 43.6532, lng: -79.3832, weight: 5, areas: ["Downtown", "Brampton", "Mississauga"] },
  { city: "San Francisco", lat: 37.7749, lng: -122.4194, weight: 3, areas: ["SoMa", "Mission"] },
  { city: "Chicago", lat: 41.8781, lng: -87.6298, weight: 2, areas: ["The Loop", "Devon Avenue"] },
  // Africa
  { city: "Nairobi", lat: -1.2921, lng: 36.8219, weight: 2, areas: ["Westlands", "Kilimani"] },
  { city: "Johannesburg", lat: -26.2041, lng: 28.0473, weight: 2, areas: ["Sandton", "Rosebank"] },
];

const VENUE_PREFIXES = [
  "Spice Route", "Tandoor House", "Curry Leaf", "The Masala Co.", "Chai Point",
  "Biryani Blues", "Urban Tadka", "Bombay Bistro", "Saffron Kitchen", "Dosa Plaza",
  "Grill Theory", "Café Mocha", "Burger Barn", "Wok Street", "The Pizza Loft",
  "Bean There", "Noodle Bar", "Kebab Korner", "Green Bowl", "Sweet Truth",
];

const CREATIVES = [
  "Diwali-Mega-Sale.mp4",
  "Summer-Combo-Offer.mp4",
  "New-Menu-Launch.jpg",
  "Weekend-Brunch-15s.mp4",
  "Loyalty-App-Download.png",
  "Monsoon-Specials.mp4",
  "Cricket-Season-Promo.mp4",
  "Healthy-Bowls-Range.jpg",
  "Late-Night-Delivery.mp4",
  "Festive-Hampers-2026.mp4",
  "Coffee-Happy-Hour.png",
  "Kids-Meal-Toy-Edition.mp4",
  "Christmas-Countdown.mp4",
  "Ramadan-Iftar-Deals.mp4",
];

const DAY_SETS: string[][] = [
  ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"],
  ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"],
  ["FRIDAY", "SATURDAY", "SUNDAY"],
  ["SATURDAY", "SUNDAY"],
];

const TIME_WINDOWS: [string, string][] = [
  ["00:00", "23:59"],
  ["11:00", "15:00"],
  ["18:00", "23:00"],
  ["07:00", "11:00"],
  ["11:00", "20:00"],
];

const DAY_MS = 24 * 60 * 60 * 1000;
const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** A campaign's flight window relative to today, picked to give each status. */
const windowFor = (status: CampaignStatus, r: () => number, today: Date) => {
  const offset = (days: number) => new Date(today.getTime() + days * DAY_MS);
  if (status === "ACTIVE") {
    return { start: offset(-Math.floor(5 + r() * 40)), end: offset(Math.floor(5 + r() * 60)) };
  }
  if (status === "UPCOMING") {
    const start = Math.floor(2 + r() * 25);
    return { start: offset(start), end: offset(start + Math.floor(14 + r() * 45)) };
  }
  const end = Math.floor(3 + r() * 60);
  return { start: offset(-end - Math.floor(14 + r() * 45)), end: offset(-end) };
};

const TARGET_SCREENS = 240;

const buildScreens = (): MapScreen[] => {
  const r = rng(20260921);
  const pick = <T>(items: T[]): T => items[Math.floor(r() * items.length)];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Each creative has one status everywhere it runs, like a real campaign.
  const creatives = CREATIVES.map((file, i) => {
    const roll = r();
    const status: CampaignStatus = roll < 0.5 ? "ACTIVE" : roll < 0.75 ? "UPCOMING" : "EXPIRED";
    const id = `demo-${(i + 1).toString().padStart(4, "0")}-${Math.floor(r() * 1e8).toString(16)}`;
    return {
      creativeId: id,
      // Real names are "<uuid>-<original file>"; displayCreativeName strips both.
      name: `${id}-${file}`,
      fileType: file.endsWith(".mp4") ? "video" : "image",
      thumbnailUrl: `https://picsum.photos/seed/${encodeURIComponent(file)}/320/180`,
      status,
      window: windowFor(status, r, today),
      // How widely the media house booked it.
      reach: 0.04 + r() * 0.26,
    };
  });

  const totalWeight = CITIES.reduce((sum, c) => sum + c.weight, 0);
  const screens: MapScreen[] = [];
  let serial = 1;

  for (const seed of CITIES) {
    let remaining = Math.max(2, Math.round((seed.weight / totalWeight) * TARGET_SCREENS));

    while (remaining > 0) {
      const area = pick(seed.areas);
      const venue = `${pick(VENUE_PREFIXES)} ${area}`;
      // Areas sit ~2-8 km from the centre; screens in a venue share its spot.
      const areaIndex = seed.areas.indexOf(area);
      const angle = (areaIndex / seed.areas.length) * 2 * Math.PI + r() * 0.6;
      const dist = 0.02 + (areaIndex % 3) * 0.025 + r() * 0.015;
      const lat = +(seed.lat + Math.sin(angle) * dist).toFixed(6);
      const lng = +(seed.lng + Math.cos(angle) * dist).toFixed(6);
      // A few venues have several screens (counter, dining hall, entrance).
      const screensHere = Math.min(remaining, r() < 0.18 ? 2 + Math.floor(r() * 3) : 1);
      // Some screens were never placed on the map — they must still be listed.
      const unplaced = r() < 0.03;

      for (let n = 0; n < screensHere; n += 1) {
        const online = r() < 0.86;
        const lastSeenAt = online
          ? new Date(Date.now() - Math.floor(r() * 90) * 1000).toISOString()
          : new Date(Date.now() - Math.floor(1 + r() * 72) * 3600 * 1000).toISOString();

        const carried = creatives.filter((c) => r() < c.reach);
        if (carried.length === 0) carried.push(pick(creatives));

        // At most one creative is the screen's now-playing item.
        const liveOnes = carried.filter((c) => c.status === "ACTIVE");
        const playingId = online && liveOnes.length && r() < 0.45 ? pick(liveOnes).creativeId : null;

        const campaigns: MapCampaign[] = carried.map((c) => {
          const [startTime, endTime] = pick(TIME_WINDOWS);
          const slot: MapSlot = {
            startDate: isoDay(c.window.start),
            endDate: isoDay(c.window.end),
            startTime,
            endTime,
            daysOfWeek: pick(DAY_SETS),
            status: c.status,
          };
          const slots = [slot];
          if (r() < 0.15) {
            const [s2, e2] = pick(TIME_WINDOWS);
            slots.push({ ...slot, startTime: s2, endTime: e2, daysOfWeek: pick(DAY_SETS) });
          }
          const ranDays =
            c.status === "UPCOMING"
              ? 0
              : Math.max(0, Math.min(Date.now(), c.window.end.getTime()) - c.window.start.getTime()) / DAY_MS;
          const plays = Math.floor(ranDays * (20 + r() * 60));
          const clip = c.fileType === "video" ? 15 + Math.floor(r() * 16) : 10;
          return {
            creativeId: c.creativeId,
            name: c.name,
            thumbnailUrl: c.thumbnailUrl,
            fileType: c.fileType,
            status: c.status,
            isPlayingNow: c.creativeId === playingId,
            plays,
            playDuration: plays * clip,
            lastPlayedAt:
              plays > 0
                ? new Date(
                    Math.min(Date.now(), c.window.end.getTime()) - Math.floor(r() * 6 * 3600 * 1000),
                  ).toISOString()
                : null,
            slots,
          };
        });

        screens.push({
          screenId: `demo-screen-${serial}`,
          screenName: screensHere > 1 ? `${venue} · Screen ${n + 1}` : `${venue}`,
          restaurantName: venue,
          city: seed.city,
          region: area,
          address: `${Math.floor(1 + r() * 250)}, ${area}, ${seed.city}`,
          latitude: unplaced ? null : lat,
          longitude: unplaced ? null : lng,
          orientation: r() < 0.25 ? "PORTRAIT" : "LANDSCAPE",
          screenOnline: online,
          lastSeenAt,
          campaigns,
        });
        serial += 1;
      }
      remaining -= screensHere;
    }
  }

  return screens;
};

let cached: MapScreen[] | null = null;
/** The fake fleet itself, shared with the Reports demo so both pages agree. */
export const demoScreens = (): MapScreen[] => (cached ??= buildScreens());

export const demoBrandScreenMap = (): BrandScreenMap => ({
  screens: demoScreens(),
  truncated: false,
});

/** The summary strip, derived from the same fake fleet so the numbers agree. */
export const demoBrandCampaignSummary = (): CampaignSummary => {
  const screens = demoScreens();
  const statusById = new Map<string, CampaignStatus>();
  let totalPlays = 0;
  let totalPlayDuration = 0;
  for (const screen of screens) {
    for (const c of screen.campaigns) {
      statusById.set(c.creativeId, c.status);
      totalPlays += c.plays;
      totalPlayDuration += c.playDuration;
    }
  }
  const statuses = [...statusById.values()];
  const count = (s: CampaignStatus) => statuses.filter((x) => x === s).length;
  return {
    totalCampaigns: statuses.length,
    activeCampaigns: count("ACTIVE"),
    upcomingCampaigns: count("UPCOMING"),
    expiredCampaigns: count("EXPIRED"),
    scheduledCampaigns: statuses.length,
    totalScreens: screens.length,
    liveScreens: screens.filter((s) => s.campaigns.some((c) => c.isPlayingNow)).length,
    totalPlays,
    totalPlayDuration,
  };
};
